import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, MoreVertical, BarChart3, Edit, Copy, Trash2, FileText, Users, CheckCircle, Zap, Share2, Sparkles, Crown, X, HelpCircle } from 'lucide-react';
import formService from '../../../services/formService';
import { getResponseCounts } from '../../../services/responseService';
import api from '../../../api';
import toast from 'react-hot-toast';
import { SkeletonMyForms } from '../../common/SkeletonLoader';
import MyFormsTour from '../MyFormsTour';
import Modal from '../../common/Modal';
import { getUserFriendlyError, ERROR_MESSAGES } from '../../../utils/errorMessages';

const FREE_FORM_LIMIT = 5;

const MyForms = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeMenu, setActiveMenu] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('free');
  const [runTour, setRunTour] = useState(false);

  // Copy form link to clipboard
  const copyFormLink = async (form) => {
    const formLink = `${window.location.origin}/f/${form.id}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(formLink);
      } else {
        // Fallback for older browsers
        const el = document.createElement('textarea');
        el.value = formLink;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      // Use the normal notification system
      alert('Form link copied to clipboard');
    } catch (err) {
      console.error('Failed to copy form link', err);
      alert('Failed to copy link');
    }
    setActiveMenu(null);
  };

  const [forms, setForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [responseCounts, setResponseCounts] = useState({});
  const [deletingFormId, setDeletingFormId] = useState(null);

  useEffect(() => {
    loadForms();
  }, []);

  // Check if user should see tour (first time with exactly 1 form)
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('fylo_myforms_tour_completed');
    if (!hasSeenTour && forms.length === 1 && !isLoading) {
      // Add delay to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        setRunTour(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [forms.length, isLoading]);

  const handleTourComplete = () => {
    localStorage.setItem('fylo_myforms_tour_completed', 'true');
    setRunTour(false);
  };

  const handleStartTour = () => {
    setRunTour(true);
  };

  const loadForms = async () => {
    setIsLoading(true);
    try {
      const [formsResponse, countsResponse, subscriptionResponse] = await Promise.all([
        formService.getForms(),
        getResponseCounts().catch(() => ({ success: true, data: {} })), // Fallback to empty if fails
        api.get('/billing/subscription').catch(() => ({ success: true, data: { plan_id: 'free' } }))
      ]);

      if (formsResponse.success) {
        setForms(formsResponse.data);
      }

      if (countsResponse.success) {
        setResponseCounts(countsResponse.data);
      }

      if (subscriptionResponse.success) {
        setCurrentPlan(subscriptionResponse.data.plan_id || 'free');
      }
    } catch (error) {
      const friendlyMessage = getUserFriendlyError(error, ERROR_MESSAGES.FORM.LOAD_FAILED);
      toast.error(friendlyMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Empty state - uncomment to test empty scenarios
  // const forms = [
  // {
  //   id: 1,
  //   title: 'Customer Feedback Quest',
  //   responses: 1204,
  //   completion: 89,
  //   status: 'Active',
  //   lastEdited: '2 hours ago',
  //   engagement: 8.7
  // },
  // {
  //   id: 2,
  //   title: 'Product Survey',
  //   responses: 876,
  //   completion: 76,
  //   status: 'Active',
  //   lastEdited: '1 day ago',
  //   engagement: 7.5
  // },
  // {
  //   id: 3,
  //   title: 'Event Registration',
  //   responses: 543,
  //   completion: 92,
  //   status: 'Active',
  //   lastEdited: '3 days ago',
  //   engagement: 9.2
  // },
  // {
  //   id: 4,
  //   title: 'User Onboarding Flow',
  //   responses: 0,
  //   completion: 0,
  //   status: 'Draft',
  //   lastEdited: '5 days ago',
  //   engagement: 0
  // },
  // ];

  const filteredForms = forms.filter(form => {
    const matchesSearch = form.title.toLowerCase().includes(searchTerm.toLowerCase());
    // Map 'active' filter to 'published' status
    const normalizedFilter = statusFilter === 'active' ? 'published' : statusFilter;
    const matchesStatus = statusFilter === 'all' || form.status?.toLowerCase() === normalizedFilter;
    return matchesSearch && matchesStatus;
  });

  const hasForms = forms.length > 0;
  const hasFilteredResults = filteredForms.length > 0;
  const hasSearchOrFilter = searchTerm || statusFilter !== 'all';

  // Format timestamp to relative time
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Create new form
  const handleCreateForm = () => {
    // Check form limit for free users
    if (currentPlan === 'free' && forms.length >= FREE_FORM_LIMIT) {
      setShowUpgradeModal(true);
      return;
    }
    navigate('/form-builder');
  };

  // Edit form
  const handleEditForm = (formId) => {
    navigate(`/form-builder/${formId}`);
    setActiveMenu(null);
  };

  // Duplicate form
  const handleDuplicateForm = async (formId) => {
    // Check form limit for free users
    if (currentPlan === 'free' && forms.length >= FREE_FORM_LIMIT) {
      setShowUpgradeModal(true);
      setActiveMenu(null);
      return;
    }

    try {
      const response = await formService.duplicateForm(formId);
      if (response.success) {
        toast.success('Form duplicated successfully');
        loadForms();
      }
    } catch (error) {
      // If backend returns 403 (form limit reached), show upgrade modal
      if (error.status === 403) {
        setShowUpgradeModal(true);
      } else {
        const friendlyMessage = getUserFriendlyError(error, ERROR_MESSAGES.FORM.DUPLICATE_FAILED);
        toast.error(friendlyMessage);
      }
    }
    setActiveMenu(null);
  };

  // Delete form
  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form? This action cannot be undone.')) {
      return;
    }

    setActiveMenu(null);
    setDeletingFormId(formId);

    try {
      const response = await formService.deleteForm(formId);
      if (response.success) {
        toast.success('Form deleted successfully');
        loadForms();
      }
    } catch (error) {
      const friendlyMessage = getUserFriendlyError(error, ERROR_MESSAGES.FORM.DELETE_FAILED);
      toast.error(friendlyMessage);
    } finally {
      setDeletingFormId(null);
    }
  };

  // View analytics
  const handleViewAnalytics = (formId) => {
    navigate(`/dashboard/analytics/${formId}`);
    setActiveMenu(null);
  };

  // Share form - copy link to clipboard
  const handleShareForm = async (formId) => {
    const formUrl = `${window.location.origin}/form/${formId}`;
    try {
      await navigator.clipboard.writeText(formUrl);
      toast.success('Form link copied to clipboard!');
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = formUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Form link copied to clipboard!');
    }
    setActiveMenu(null);
  };

  // Empty state content based on different scenarios
  const getEmptyStateContent = () => {
    // No forms at all
    if (!hasForms) {
      return {
        icon: FileText,
        title: "No forms created yet",
        description: "Get started by creating your first form to collect responses and insights",
        buttonText: "Create Your First Form",
        showAction: true
      };
    }

    // No results after search/filter
    if (hasSearchOrFilter && !hasFilteredResults) {
      return {
        icon: Search,
        title: "No forms found",
        description: `No forms match "${searchTerm}"${statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}. Try adjusting your search or filter.`,
        buttonText: "Clear Filters",
        showAction: true
      };
    }

    return null;
  };

  const emptyState = getEmptyStateContent();

  const handleEmptyStateAction = () => {
    if (!hasForms) {
      handleCreateForm();
    } else if (hasSearchOrFilter) {
      // Clear filters
      setSearchTerm('');
      setStatusFilter('all');
    }
  };

  // Loading skeleton
  if (isLoading) {
    return <SkeletonMyForms variant="shimmer" />;
  }

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8 p-3 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0 px-2 sm:px-0">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-1 sm:mb-2 break-words">My Forms</h1>
          <p className="text-gray-600 text-sm sm:text-base lg:text-lg truncate">Manage and track all your forms</p>
        </div>

        {hasForms && (
          <button
            onClick={handleCreateForm}
            className="flex items-center gap-2 sm:gap-3 px-4 py-2 sm:px-6 sm:py-3 bg-blue-600 text-white rounded-lg sm:rounded-xl hover:bg-blue-700 transition-all duration-300 border-0 w-full sm:w-auto justify-center sm:justify-start flex-shrink-0"
            data-tour="create-form-button"
          >
            <Plus size={18} className="sm:w-5 sm:h-5" />
            <span className="font-semibold text-sm sm:text-base whitespace-nowrap">Create New Form</span>
          </button>
        )}
      </div>

      {/* Filters - Only show when there are forms */}
      {hasForms && (
        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Search */}
            <div className="relative flex-1" data-tour="search-forms">
              <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search forms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-lightGray border-0 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-blue/50 transition-all duration-300 text-sm sm:text-base"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2 sm:gap-3" data-tour="filter-forms">
              <Filter size={18} className="text-gray-400 flex-shrink-0" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 sm:px-4 py-2.5 sm:py-3 bg-lightGray border-0 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-blue/50 transition-all duration-300 text-sm sm:text-base w-full sm:w-auto"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {emptyState && (
        <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 p-6 sm:p-8 lg:p-12 text-center">
          <div className="max-w-md mx-auto">
            <emptyState.icon size={48} className="mx-auto text-gray-300 mb-4 sm:mb-6 sm:w-16 sm:h-16" />
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3 px-2">
              {emptyState.title}
            </h3>
            <p className="text-gray-500 text-sm sm:text-base lg:text-lg mb-6 sm:mb-8 leading-relaxed px-4">
              {emptyState.description}
            </p>
            {emptyState.showAction && (
              <button
                onClick={handleEmptyStateAction}
                className="inline-flex items-center gap-2 sm:gap-3 px-6 py-3 sm:px-8 sm:py-4 bg-blue-600 text-white rounded-lg sm:rounded-xl hover:bg-blue/90 transition-all duration-300 border-0 font-semibold text-base sm:text-lg"
              >
                <Plus size={18} className="sm:w-5 sm:h-5" />
                {emptyState.buttonText}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Forms Grid */}
      {hasFilteredResults && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {filteredForms.map((form, index) => (
            <div
              key={form.id}
              className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 p-4 sm:p-6 transition-all duration-300 hover:border-blue/30 group relative"
              data-tour={index === 0 ? "form-card" : undefined}
            >
              {/* Deleting Overlay */}
              {deletingFormId === form.id && (
                <div className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl z-20 flex items-center justify-center">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-red-500 border-t-transparent mb-2"></div>
                    <p className="text-sm font-semibold text-red-600">Deleting...</p>
                  </div>
                </div>
              )}
              {/* Card Header */}
              <div className="flex items-start justify-between mb-4 sm:mb-6 gap-2">
                <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="p-1.5 sm:p-2 bg-lightGray rounded-lg flex-shrink-0">
                    <FileText size={18} className="text-blue sm:w-5 sm:h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2 leading-tight break-words">{form.title}</h3>
                    <span
                      className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold rounded-full ${form.status === 'published'
                        ? 'bg-greenBtn/20 text-greenBtn'
                        : form.status === 'draft'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-600'
                        }`}
                    >
                      {form.status === 'published' ? 'Published' : form.status === 'draft' ? 'Draft' : form.status}
                    </span>
                  </div>
                </div>

                {/* Actions Menu */}
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => setActiveMenu(activeMenu === form.id ? null : form.id)}
                    className="p-1.5 sm:p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-lightGray transition-all duration-300"
                    data-tour={index === 0 ? "form-actions" : undefined}
                  >
                    <MoreVertical size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </button>

                  {activeMenu === form.id && (
                    <div className="absolute right-0 mt-2 w-40 sm:w-44 md:w-48 bg-white rounded-xl border border-gray-100 py-2 z-10 shadow-lg">
                      <button
                        onClick={() => copyFormLink(form)}
                        className="flex items-center gap-2 sm:gap-3 w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700 hover:bg-lightGray transition-colors duration-200"
                        data-tour={index === 0 ? "share-form" : undefined}
                      >
                        <Share2 size={14} className="sm:w-4 sm:h-4" />
                        Share Form
                      </button>
                      <button
                        onClick={() => handleViewAnalytics(form.id)}
                        className="flex items-center gap-2 sm:gap-3 w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700 hover:bg-lightGray transition-colors duration-200"
                        data-tour={index === 0 ? "view-responses" : undefined}
                      >
                        <BarChart3 size={14} className="sm:w-4 sm:h-4" />
                        View Analytics
                      </button>
                      <button
                        onClick={() => handleEditForm(form.id)}
                        className="flex items-center gap-2 sm:gap-3 w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700 hover:bg-lightGray transition-colors duration-200"
                      >
                        <Edit size={14} className="sm:w-4 sm:h-4" />
                        Edit Form
                      </button>
                      <button
                        onClick={() => handleDuplicateForm(form.id)}
                        className="flex items-center gap-2 sm:gap-3 w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-700 hover:bg-lightGray transition-colors duration-200"
                      >
                        <Copy size={14} className="sm:w-4 sm:h-4" />
                        Duplicate
                      </button>
                      <hr className="my-2 border-gray-100" />
                      <button
                        onClick={() => handleDeleteForm(form.id)}
                        className="flex items-center gap-2 sm:gap-3 w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-red-500 hover:bg-red-50 transition-colors duration-200"
                      >
                        <Trash2 size={14} className="sm:w-4 sm:h-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                    <Users size={14} className="text-gray-400 flex-shrink-0 sm:w-4 sm:h-4" />
                    <span className="text-xs sm:text-sm text-gray-600 truncate">Questions</span>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900 flex-shrink-0">
                    {form.questions?.length || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                    <CheckCircle size={14} className="text-gray-400 flex-shrink-0 sm:w-4 sm:h-4" />
                    <span className="text-xs sm:text-sm text-gray-600 truncate">Responses</span>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-greenBtn flex-shrink-0">
                    {responseCounts[form.id] || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                    <Zap size={14} className="text-gray-400 flex-shrink-0 sm:w-4 sm:h-4" />
                    <span className="text-xs sm:text-sm text-gray-600 truncate">Status</span>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-mint capitalize flex-shrink-0">
                    {form.status === 'published' ? 'Published' : form.status === 'draft' ? 'Draft' : form.status}
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-3 sm:pt-4 border-t border-gray-100">
                <p className="text-[10px] sm:text-xs text-gray-500 truncate">Last edited {getRelativeTime(form.updated_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Stats for Empty State with Forms */}
      {hasForms && hasFilteredResults && filteredForms.length > 0 && (
        <div className="bg-blue-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            <div className="text-center md:text-left">
              <div className="text-blue-100 text-xs sm:text-sm font-medium mb-1 sm:mb-2 uppercase tracking-wide truncate">
                Total Forms
              </div>
              <div className="text-2xl sm:text-3xl font-bold">{forms.length}{currentPlan === 'free' && `/${FREE_FORM_LIMIT}`}</div>
            </div>
            <div className="text-center md:text-left border-l-0 md:border-l border-blue-400/30 md:pl-8">
              <div className="text-blue-100 text-xs sm:text-sm font-medium mb-1 sm:mb-2 uppercase tracking-wide truncate">
                Active Forms
              </div>
              <div className="text-2xl sm:text-3xl font-bold">
                {forms.filter(f => f.status === 'published').length}
              </div>
            </div>
            <div className="text-center md:text-left border-l-0 md:border-l border-blue-400/30 md:pl-8">
              <div className="text-blue-100 text-xs sm:text-sm font-medium mb-1 sm:mb-2 uppercase tracking-wide truncate">
                Total Responses
              </div>
              <div className="text-2xl sm:text-3xl font-bold">
                {forms.reduce((sum, form) => sum + (responseCounts[form.id] || 0), 0).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <Modal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        showCloseButton={false}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Crown size={20} className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Upgrade Your Plan</h3>
            </div>
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles size={32} className="text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                You've reached your form limit!
              </h4>
              <p className="text-gray-600 text-sm">
                Free accounts can create up to {FREE_FORM_LIMIT} forms. Upgrade to Creator to unlock unlimited forms and more amazing features!
              </p>
            </div>

            <div className="bg-blue-50 rounded-xl p-4 space-y-2">
              <p className="text-sm font-semibold text-blue-900">Creator Plan includes:</p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-blue-600" />
                  Unlimited forms
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-blue-600" />
                  Unlimited responses
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-blue-600" />
                  Advanced analytics
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-blue-600" />
                  Priority support
                </li>
              </ul>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-semibold"
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/dashboard/account?section=plan');
                }}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold flex items-center justify-center gap-2"
              >
                <Zap size={18} />
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Help Button - Start Tour */}
      {hasForms && (
        <button
          onClick={handleStartTour}
          className="fixed bottom-4 right-4 w-12 h-12 bg-white text-blue-600 rounded-full border-2 border-blue-600 hover:bg-blue-50 transition-all duration-300 flex items-center justify-center z-40 shadow-lg"
          title="Start Tour"
          aria-label="Start Tour"
        >
          <HelpCircle className="w-6 h-6" />
        </button>
      )}

      {/* Product Tour */}
      <MyFormsTour run={runTour} onComplete={handleTourComplete} />
    </div>
  );
};

export default MyForms;